import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { useSeo } from "../components/site/Layout";
import { Section } from "../components/site/blocks";
import { Field, inputProps, Honeypot } from "../components/site/FormField";
import { PaymentConsents, validateConsents } from "../components/site/PaymentConsents";
import { api, formatApiError, EMAIL_RE, PHONE_RE, CP_RE } from "../lib/api";
import { euros } from "../data/site";

export default function ForfaitCheckout() {
  const { id } = useParams();
  const [forfait, setForfait] = useState(undefined);
  const [f, setF] = useState({ name: "", email: "", phone: "", address: "", postal_code: "", city: "", website: "" });
  const [profile, setProfile] = useState("particulier");
  const [cgv, setCgv] = useState(false);
  const [ack, setAck] = useState(false);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");
  useSeo(forfait ? `Paiement — ${forfait.name}` : "Paiement en ligne", "Paiement sécurisé par carte d'un forfait Habitalys.");
  useEffect(() => { api.get(`/forfaits/${id}`).then((r) => setForfait(r.data)).catch(() => setForfait(null)); }, [id]);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    const e = {};
    if (f.name.trim().length < 2) e.name = "Merci d'indiquer votre nom.";
    if (!EMAIL_RE.test(f.email.trim())) e.email = "Adresse email invalide.";
    if (f.phone.trim() && !PHONE_RE.test(f.phone.trim())) e.phone = "Numéro invalide (exemple : 06 12 34 56 78).";
    if (f.address.trim().length < 4) e.address = "Merci d'indiquer votre adresse de facturation.";
    if (!CP_RE.test(f.postal_code.trim())) e.postal_code = "Code postal à 5 chiffres requis.";
    if (f.city.trim().length < 2) e.city = "Merci d'indiquer la ville.";
    Object.assign(e, validateConsents(profile, cgv, ack));
    setErrors(e); setServerError("");
    if (Object.keys(e).length) return;
    setLoading(true);
    try {
      const r = await api.post("/payments/checkout/forfait", { ...f, phone: f.phone.trim() || null, forfait_id: id, profile, cgv, withdrawal_ack: ack, origin_url: window.location.origin });
      window.location.href = r.data.url;
    } catch (err) { setServerError(formatApiError(err.response?.data?.detail)); setLoading(false); }
  };

  if (forfait === null) return (
    <Section testid="forfait-unavailable"><h1 className="text-3xl">Ce forfait n'est plus disponible</h1><Link to="/tarifs" className="btn-primary mt-6">Voir les tarifs</Link></Section>
  );
  return (
    <Section className="bg-mist-50" testid="forfait-checkout-page">
      <Link to="/tarifs" className="inline-flex items-center gap-2 font-semibold text-navy"><ArrowLeft className="h-5 w-5" aria-hidden="true" /> Tous les tarifs</Link>
      <div className="mt-6 grid lg:grid-cols-12 gap-10">
        <aside className="lg:col-span-4 order-first lg:order-last">
          <div className="rounded-[28px] bg-navy text-mist p-7 lg:sticky lg:top-32" data-testid="checkout-summary">
            <p className="text-mist/80 text-sm font-semibold uppercase tracking-wider">Récapitulatif</p>
            <h2 className="mt-3 font-display text-2xl text-mist">{forfait?.name || "…"}</h2>
            <p className="mt-3 text-mist/85 text-[15px]">{forfait?.description}</p>
            <p className="mt-6 font-display text-5xl text-mist" data-testid="checkout-amount">{forfait ? euros(forfait.price_cents) : "…"} <span className="text-base font-sans">TTC</span></p>
            <p className="mt-6 flex gap-2 text-sm text-mist/80"><Lock className="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" /> Paiement sécurisé par Stripe. Le montant est calculé par nos serveurs.</p>
          </div>
        </aside>
        <form noValidate onSubmit={submit} className="relative lg:col-span-8 rounded-[28px] bg-white border border-navy/10 p-6 sm:p-10 space-y-6" data-testid="forfait-checkout-form">
          <h1 className="text-3xl sm:text-4xl">Vos coordonnées de facturation</h1>
          <div className="grid sm:grid-cols-2 gap-5">
            <Field id="name" label="Nom et prénom" required error={errors.name}><input {...inputProps("name", errors)} value={f.name} onChange={set("name")} autoComplete="name" /></Field>
            <Field id="email" label="Email" required error={errors.email}><input {...inputProps("email", errors)} type="email" value={f.email} onChange={set("email")} autoComplete="email" /></Field>
            <Field id="phone" label="Téléphone (facultatif)" error={errors.phone}><input {...inputProps("phone", errors)} type="tel" value={f.phone} onChange={set("phone")} autoComplete="tel" /></Field>
            <Field id="address" label="Adresse" required error={errors.address}><input {...inputProps("address", errors)} value={f.address} onChange={set("address")} autoComplete="street-address" /></Field>
            <Field id="postal_code" label="Code postal" required error={errors.postal_code}><input {...inputProps("postal_code", errors)} inputMode="numeric" maxLength={5} value={f.postal_code} onChange={set("postal_code")} autoComplete="postal-code" /></Field>
            <Field id="city" label="Ville" required error={errors.city}><input {...inputProps("city", errors)} value={f.city} onChange={set("city")} autoComplete="address-level2" /></Field>
          </div>
          <Honeypot value={f.website} onChange={set("website")} />
          <PaymentConsents profile={profile} setProfile={setProfile} cgv={cgv} setCgv={setCgv} ack={ack} setAck={setAck} errors={errors} />
          {serverError && <p role="alert" data-testid="checkout-server-error" className="rounded-xl bg-red-50 text-red-800 p-4 font-medium">{serverError}</p>}
          <button type="submit" className="btn-accent w-full sm:w-auto text-lg px-8 py-4" disabled={loading || !forfait} data-testid="checkout-submit-button">
            {loading ? <Loader2 className="h-5 w-5 animate-spin" aria-hidden="true" /> : <Lock className="h-5 w-5" aria-hidden="true" />} Payer {forfait ? euros(forfait.price_cents) : ""} par carte
          </button>
        </form>
      </div>
    </Section>
  );
}
